import React, { useState } from 'react'
import { Group, NumberInput, Row, Toggle } from './ui'
import { useT } from './translations'

const NUMERIC = ['number', 'float']

const newKey = (columns) => {
  const keys = new Set(columns.map((column) => column.key))
  let index = columns.length + 1
  while (keys.has(`c${index}`)) { index += 1 }
  return `c${index}`
}

const TableEditor = ({ config, onChange, hasPdf, meta, disabled }) => {
  const t = useT()
  const columns = config.columns || []
  const set = (changes) => onChange({ ...config, ...changes })
  const setColumn = (index, changes) => set({ columns: columns.map((column, position) => (position === index ? { ...column, ...changes } : column)) })

  const move = (index, offset) => {
    const target = index + offset
    if (target < 0 || target >= columns.length) { return }
    const next = [...columns]
    ;[next[index], next[target]] = [next[target], next[index]]
    set({ columns: next })
  }

  // An empty line is never saved, so a new one stays here until it is typed in
  const [blankRows, setBlankRows] = useState(0)
  const defaultRows = config.default_rows || []
  const shownRows = [...defaultRows, ...Array.from({ length: blankRows }, () => ({}))]
  const setDefaultCell = (rowIndex, column, raw) => {
    const rows = defaultRows.map((row) => ({ ...row }))
    if (rowIndex >= rows.length) {
      rows.push({})
      setBlankRows((count) => Math.max(0, count - 1))
      rowIndex = rows.length - 1
    }
    let value = raw
    if (raw === '') { value = undefined } else if (NUMERIC.includes(column.type)) { value = Number(raw) } else if (column.type === 'yes_no') { value = raw === 'true' }
    if (value === undefined) { delete rows[rowIndex][column.key] } else { rows[rowIndex][column.key] = value }
    set({ default_rows: rows })
  }

  const given  = columns.reduce((sum, column) => sum + (column.width > 0 ? column.width : 0), 0)
  const unset  = columns.filter((column) => !(column.width > 0)).length
  const filler = unset === 0 ? 0 : Math.max((100 - given) / unset, 100 / Math.max(columns.length, 1) / 2)
  const total  = columns.reduce((sum, column) => sum + (column.width > 0 ? column.width : filler), 0) || 1

  return (
    <>
      <Group
        title={t.columns}
        actions={!disabled && (
          <button type="button" className="fb-link" onClick={() => set({ columns: [...columns, { key: newKey(columns), name: `${t.column_default} ${columns.length + 1}`, type: 'text', width: null, options: [], source: null }] })}>
            <i className="fas fa-plus" /> {t.add_column}
          </button>
        )}
      >
        <div className="fb-width-bar">
          {columns.map((column) => (
            <div key={column.key} style={{ width: `${((column.width > 0 ? column.width : filler) * 100) / total}%` }} title={column.name}>
              <span>{column.name || '—'}</span>
            </div>
          ))}
        </div>

        {columns.map((column, index) => (
          <div className="fb-column" key={column.key}>
            <div className="fb-column-head">
              <span className="fb-column-index">{index + 1}</span>
              <input
                className="fb-input"
                value={column.name}
                placeholder={t.column_name}
                disabled={disabled}
                onChange={(event) => setColumn(index, { name: event.target.value })}
              />
              {!disabled &&
                <div className="fb-option-actions">
                  <button type="button" title={t.move_up} disabled={index === 0} onClick={() => move(index, -1)}><i className="fas fa-arrow-up" /></button>
                  <button type="button" title={t.move_down} disabled={index === columns.length - 1} onClick={() => move(index, 1)}><i className="fas fa-arrow-down" /></button>
                  <button type="button" title={t.delete} onClick={() => set({ columns: columns.filter((_, position) => position !== index) })}><i className="fas fa-times" /></button>
                </div>
              }
            </div>
            <div className="fb-column-body">
              <select
                className="fb-input"
                value={column.type}
                disabled={disabled}
                onChange={(event) => setColumn(index, { type: event.target.value })}
              >
                {meta.columnTypes.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
              <NumberInput value={column.width} min={1} max={100} suffix="%" placeholder={t.auto} disabled={disabled} onChange={(width) => setColumn(index, { width })} />
              {config.prefill_with &&
                <select
                  className="fb-input"
                  value={column.source || ''}
                  disabled={disabled}
                  onChange={(event) => setColumn(index, { source: event.target.value || null })}
                >
                  <option value="">{t.no_source}</option>
                  {meta.sources.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
              }
            </div>
            {column.type === 'select' &&
              <input
                className="fb-input is-sub"
                value={(column.options || []).join('; ')}
                placeholder={t.choices}
                disabled={disabled}
                onChange={(event) => setColumn(index, { options: event.target.value.split(';').map((option) => option.trim()).filter(Boolean) })}
              />
            }
          </div>
        ))}
        {columns.length === 0 && <div className="fb-empty">{t.no_columns}</div>}
      </Group>

      <Group title={t.lines}>
        <div className="fb-grid-2">
          <Row label={t.min_rows}><NumberInput value={config.min_rows} min={0} max={100} disabled={disabled} onChange={(value) => set({ min_rows: value })} /></Row>
          <Row label={t.max_rows}><NumberInput value={config.max_rows} min={1} max={100} disabled={disabled} onChange={(value) => set({ max_rows: value })} /></Row>
        </div>
        {hasPdf &&
          <>
            <Row label={t.pdf_rows} hint={t.pdf_rows_hint}>
              <NumberInput value={config.pdf_rows} min={1} max={100} disabled={disabled} onChange={(value) => set({ pdf_rows: value })} />
            </Row>
            <Toggle checked={config.print_header} disabled={disabled} label={t.print_header} onChange={(value) => set({ print_header: value })} />
          </>
        }
        <Toggle checked={config.show_totals} disabled={disabled} label={t.show_totals} hint={t.show_totals_hint} onChange={(value) => set({ show_totals: value })} />
        <Row label={t.prefill_with}>
          <select className="fb-input" value={config.prefill_with || ''} disabled={disabled} onChange={(event) => set({ prefill_with: event.target.value || null })}>
            <option value="">{t.no_prefill}</option>
            {meta.tablePrefills.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </Row>
      </Group>

      {columns.length > 0 &&
        <Group
          title={t.default_rows}
          actions={!disabled && (
            <button type="button" className="fb-link" onClick={() => setBlankRows(blankRows + 1)}>
              <i className="fas fa-plus" /> {t.add_line}
            </button>
          )}
        >
          {shownRows.length === 0 && <div className="fb-prop-hint">{t.default_rows_hint}</div>}
          {shownRows.map((row, rowIndex) => (
            <div className="fb-default-row" key={rowIndex}>
              {columns.map((column) => (
                column.type === 'select' || column.type === 'yes_no'
                  ? (
                    <select key={column.key} className="fb-input" title={column.name} disabled={disabled}
                            value={row[column.key] === undefined ? '' : String(row[column.key])}
                            onChange={(event) => setDefaultCell(rowIndex, column, event.target.value)}>
                      <option value="">{column.name}</option>
                      {(column.type === 'yes_no' ? [['true', t.yes_label], ['false', t.no_label]] : (column.options || []).map((option) => [option, option]))
                        .map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                    </select>
                  )
                  : (
                    <input key={column.key} className="fb-input" title={column.name} placeholder={column.name} disabled={disabled}
                           type={NUMERIC.includes(column.type) ? 'number' : column.type === 'date' ? 'date' : 'text'}
                           value={row[column.key] === undefined ? '' : row[column.key]}
                           onChange={(event) => setDefaultCell(rowIndex, column, event.target.value)} />
                  )
              ))}
              {!disabled &&
                <button type="button" className="fb-icon-button is-small" title={t.delete}
                        onClick={() => (rowIndex >= defaultRows.length
                          ? setBlankRows(blankRows - 1)
                          : set({ default_rows: defaultRows.filter((_, position) => position !== rowIndex) }))}>
                  <i className="fas fa-times" />
                </button>
              }
            </div>
          ))}
        </Group>
      }
    </>
  )
}

export default TableEditor
